import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Star } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { FullPageLoader } from "../../components/RouteGuards";
import { StatusBadge } from "../../components/register/UnderReview";
import { WizardCtx } from "../../components/register/WizardContext";
import StepReview from "../../components/register/steps/StepReview";
import { ProfileActions } from "../../components/admin/detail/ProfileActions";
import { FlagButton } from "../../components/admin/detail/FlagButton";
import { EditProfileForm } from "../../components/admin/detail/EditProfileForm";
import { OfficeDetails } from "../../components/admin/detail/OfficeDetails";
import { AdminNotes } from "../../components/admin/detail/AdminNotes";
import { PendingEdits } from "../../components/admin/detail/PendingEdits";
import { MediaCard } from "../../components/admin/MediaCard";
import { useAuth } from "../../context/AuthContext";
import { canDo, fmtDate } from "../../lib/adminApi";
import { fromRecords } from "../../lib/formData";
import { supabase } from "../../lib/supabase";

const one = (table, id) => supabase.from(table).select("*").eq("profile_id", id).maybeSingle();

function MediaTab({ rec, reload }) {
  const auth = useAuth();
  return (
    <div className="space-y-6">
      <section className="rmm-card p-5 sm:p-6">
        <h3 className="mb-4 font-display text-2xl font-semibold text-[#7A0016]">Photos</h3>
        {rec.photos.length === 0 ? <p className="text-sm text-[#8C6A6C]">No photos uploaded.</p> : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {rec.photos.map((p) => <MediaCard key={p.id} item={p} kind="photo" canReview={canDo(auth, "can_approve")} onChanged={reload} />)}
          </div>
        )}
      </section>
      <section className="rmm-card p-5 sm:p-6">
        <h3 className="mb-4 font-display text-2xl font-semibold text-[#7A0016]">ID proof</h3>
        {!canDo(auth, "can_verify") ? <p className="text-sm text-[#8C6A6C]">You do not have permission to view ID proofs.</p>
          : rec.docs.length === 0 ? <p className="text-sm text-[#8C6A6C]">No ID proof uploaded.</p> : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {rec.docs.map((d) => <MediaCard key={d.id} item={d} kind="doc" canReview onChanged={reload} />)}
            </div>
          )}
      </section>
    </div>
  );
}

export default function ProfileDetail() {
  const { id } = useParams();
  const auth = useAuth();
  const [rec, setRec] = useState(null);
  const [tab, setTab] = useState("overview");
  const canVerify = canDo(auth, "can_verify");

  const load = useCallback(async () => {
    const [p, family, priv, prefs, office, photos, docs, edits] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", id).maybeSingle(),
      one("family_details", id), one("profile_private_details", id), one("partner_preferences", id), one("profile_office_details", id),
      supabase.from("profile_photos").select("*").eq("profile_id", id).order("is_primary", { ascending: false }),
      canVerify ? supabase.from("verification_documents").select("*").eq("profile_id", id).order("created_at", { ascending: false }) : Promise.resolve({ data: [] }),
      supabase.from("profile_edits").select("*").eq("profile_id", id).is("reviewed_at", null).order("created_at"),
    ]);
    if (!p.data) return setRec(false);
    setRec({ profile: p.data, family: family.data, priv: priv.data, prefs: prefs.data, office: office.data, photos: photos.data || [], docs: docs.data || [], edits: edits.data || [] });
  }, [id, canVerify]);
  useEffect(() => { load(); }, [load]);

  const canEdit = canDo(auth, "can_edit");
  const viewCtx = useMemo(() => rec && ({ data: fromRecords(rec), set: () => {}, errors: {}, userId: null, mode: "admin", goTo: () => canEdit && setTab("edit") }), [rec, canEdit]);

  if (rec === null) return <FullPageLoader />;
  if (rec === false) return <p data-testid="profile-not-found" className="rmm-card p-8 text-sm text-[#8C6A6C]">Profile not found or you do not have access.</p>;
  const p = rec.profile;
  return (
    <div data-testid="profile-detail-page">
      <Link to="/admin/profiles" data-testid="back-to-profiles" className="mb-4 inline-flex items-center gap-1 text-sm text-[#8C6A6C] hover:text-[#7A0016]"><ArrowLeft className="h-4 w-4" /> All profiles</Link>
      <div className="rmm-card mb-6 flex flex-wrap items-start justify-between gap-4 p-5 sm:p-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">{p.registration_no} · {p.profile_for}</p>
          <h1 className="mt-1 flex flex-wrap items-center gap-2 font-display text-3xl font-bold text-[#4A000E] sm:text-4xl">
            {p.full_name}
            {p.is_verified && <span data-testid="verified-badge" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-sans text-xs font-semibold text-emerald-800"><BadgeCheck className="h-3.5 w-3.5" /> Verified</span>}
            {p.is_featured && <span data-testid="featured-badge" className="inline-flex items-center gap-1 rounded-full bg-[#F5E6BA] px-2 py-0.5 font-sans text-xs font-semibold text-[#7A0016]"><Star className="h-3.5 w-3.5" /> Featured</span>}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-[#8C6A6C]">
            <StatusBadge status={p.status} /> <span>Registered {fmtDate(p.created_at)}</span> {!p.user_id && <span>· Created by office</span>}
          </div>
          {p.rejection_reason && <p data-testid="status-reason" className="mt-2 text-sm text-[#A31828]">Reason: {p.rejection_reason}</p>}
        </div>
        <div className="flex flex-col items-end gap-3">
          <ProfileActions profile={p} onChanged={load} />
          <FlagButton profileId={p.id} />
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex h-auto flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          {canEdit && <TabsTrigger value="edit" data-testid="tab-edit">Edit</TabsTrigger>}
          <TabsTrigger value="office" data-testid="tab-office">Office</TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-media">Photos &amp; ID</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-6">
          <PendingEdits edits={rec.edits} />
          <WizardCtx.Provider value={viewCtx}><StepReview hideUploads /></WizardCtx.Provider>
        </TabsContent>
        {canEdit && <TabsContent value="edit"><EditProfileForm key={p.updated_at} rec={rec} onSaved={load} /></TabsContent>}
        <TabsContent value="office"><OfficeDetails key={rec.office?.updated_at || "new"} profileId={p.id} office={rec.office} canEdit={canEdit} onSaved={load} /></TabsContent>
        <TabsContent value="media"><MediaTab rec={rec} reload={load} /></TabsContent>
        <TabsContent value="notes"><AdminNotes profileId={p.id} /></TabsContent>
      </Tabs>
    </div>
  );
}
